/**
 * Wave A · Mobile resilience pass.
 *
 * OfflineBanner — sticky top bar shown when navigator.onLine === false.
 * Slides down on mount with a 220ms ease-out cubic-bezier(0.23, 1, 0.32, 1)
 * and disappears when the connection is restored.
 *
 * Tokens: bg-warn/10, border-warn/40, text-warn from DESIGN.md.
 * Mobile: safe-area-top padding, text-[13px].
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

function WifiOffIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
      <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
      <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
      <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" />
    </svg>
  );
}

interface OfflineBannerProps {
  className?: string;
}

export function OfflineBanner({ className }: OfflineBannerProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  const [visible, setVisible] = useState<boolean>(isOffline);

  useEffect(() => {
    function onOnline() {
      setIsOffline(false);
      // Keep the element in DOM briefly so the slide-up animation plays.
      const t = window.setTimeout(() => setVisible(false), 300);
      return () => window.clearTimeout(t);
    }
    function onOffline() {
      setIsOffline(true);
      setVisible(true);
    }

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={isEn ? 'No connection' : 'Sem conexão'}
      data-offline-banner
      className={cn(
        // Sticky at the very top, above any page header.
        'sticky top-0 z-50',
        'border-b border-warn/40 bg-warn/10',
        'px-4 pt-[env(safe-area-inset-top)]',
        // Slide-down on mount, slide-up on leave.
        'transition-transform duration-[220ms]',
        isOffline
          ? 'translate-y-0 ease-[cubic-bezier(0.23,1,0.32,1)]'
          : '-translate-y-full ease-[cubic-bezier(0.23,1,0.32,1)]',
        className,
      )}
    >
      <div className="flex min-h-[36px] items-center gap-2 py-2">
        <WifiOffIcon className="h-3.5 w-3.5 flex-shrink-0 text-warn" />
        <span className="text-[13px] font-medium text-warn">
          {isEn ? 'No connection · showing saved data · trying to reconnect' : 'Sem conexão · mostrando dados salvos · tentando reconectar'}
        </span>
      </div>
    </div>
  );
}
